import { useReducedMotion, type FeatureBundle, type Transition, type Variants } from 'framer-motion'

/**
 * Feature bundle stiže zasebnim chunkom nakon prvog prikaza — u početnom paketu
 * ostaje samo mali `m` runtime. Popis uređaja je najvažniji ekran na mobitelu i
 * ne smije platiti punu animacijsku biblioteku prije nego se iscrta.
 */
export const loadMotionFeatures = (): Promise<FeatureBundle> =>
  import('framer-motion').then((mod) => mod.domMax)

/**
 * Zajednički pokret za Računko.
 *
 * Motion je funkcionalan: potvrđuje da se popis promijenio, da je red nestao ili
 * da je novi rok stigao. Nema dekorativnih ulaza. `prefers-reduced-motion` gasi
 * sve pomake i ostavlja samo trenutačnu promjenu stanja.
 */

/** Eksponencijalni ease-out — brz start, mirno slijetanje. */
export const EASE_OUT = [0.16, 1, 0.3, 1] as const

export const SPRING: Transition = { type: 'spring', stiffness: 420, damping: 34, mass: 0.7 }

export const QUICK: Transition = { duration: 0.22, ease: EASE_OUT }

/** Ulaz/izlaz stavke popisa. Kreće iz gotovo vidljivog stanja, ne iz ničega. */
export const listItem: Variants = {
  hidden: { opacity: 0, y: 8 },
  shown: { opacity: 1, y: 0 },
  gone: { opacity: 0, y: -6, transition: { duration: 0.14, ease: EASE_OUT } },
}

/** Stagger držimo ispod praga percepcije kašnjenja. */
export const listContainer: Variants = {
  hidden: {},
  shown: { transition: { staggerChildren: 0.022, delayChildren: 0.02 } },
}

export interface MotionPreset {
  reduced: boolean
  /** Props za spremnik popisa. */
  container: { variants: Variants; initial: string; animate: string }
  /** Props za pojedinu stavku popisa. */
  item: { variants: Variants; initial: string; animate: string; exit: string; transition: Transition }
  /** Layout animacija pri filtriranju/sortiranju — isključena uz reduced motion. */
  layout: boolean
  transition: Transition
}

export function useMotionPreset(): MotionPreset {
  const reduced = useReducedMotion() ?? false
  const still: Variants = { hidden: {}, shown: {}, gone: {} }
  return {
    reduced,
    container: {
      variants: reduced ? still : listContainer,
      initial: 'hidden',
      animate: 'shown',
    },
    item: {
      variants: reduced ? still : listItem,
      initial: 'hidden',
      animate: 'shown',
      exit: 'gone',
      transition: reduced ? { duration: 0 } : SPRING,
    },
    layout: !reduced,
    transition: reduced ? { duration: 0 } : QUICK,
  }
}
